// @ts-nocheck
import React from 'react'
import { useTranslation } from 'react-i18next'
import './App.css'
import { Toaster } from "@/components/ui/toaster"
import { Toaster as SonnerToaster } from "sonner"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import VisualEditAgent from '@/lib/VisualEditAgent'
import NavigationTracker from '@/lib/NavigationTracker'
import { pagesConfig } from './pages.config'
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { ErrorPage } from '@/components/common/ErrorPage';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import { AuthBootstrap } from '@/components/common/AuthBootstrap';
import { RequireRole as RequireRoleV2 } from '@/components/common/RequireRole';
import { RequireSpace } from '@/components/common/RequireSpace';
import { DevOrSuperAdmin } from '@/components/common/DevOrSuperAdmin';
import { LegacyLoginRedirect } from '@/components/common/LegacyLoginRedirect';
import AdminOrganisations from '@/pages/AdminOrganisations';
import AdminPermissions from '@/pages/AdminPermissions';
import AdminSupervision from '@/pages/AdminSupervision';
import AdminAuditLogs from '@/pages/AdminAuditLogs';
import AdminClientErrors from '@/pages/AdminClientErrors';
import AdminPlatformHealth from '@/pages/AdminPlatformHealth';
import SuperAdminLayout from '@/layouts/SuperAdminLayout';
// DUP-3 (W6): one Team page in the shell of whoever opens it.
import TeamShell, { TeamManagementRedirect, OrganisationMembersRedirect } from '@/layouts/TeamShell';
import PrivacyPolicy from '@/pages/legal/PrivacyPolicy';
import TermsOfService from '@/pages/legal/TermsOfService';
import { PORTAL_REDIRECTS, portalTabUrl } from '@/lib/portalTabs';

// VF: Dialer multi-screen routing
import { DialerProvider } from '@/contexts/DialerContext';
import DialerLayout from '@/layouts/DialerLayout';
import HomeScreen from '@/pages/dialer/HomeScreen';
import ContactsScreen from '@/pages/dialer/ContactsScreen';
import FollowupsScreen from '@/pages/dialer/FollowupsScreen';
import SmsScreen from '@/pages/dialer/SmsScreen';
import AnalyticsScreen from '@/pages/dialer/AnalyticsScreen';
import TeamManagement from '@/pages/TeamManagement';
import ToolsScreen from '@/pages/dialer/ToolsScreen';
import CRMScreen from '@/pages/dialer/CRMScreen';
import ClientPortalScreen from '@/pages/dialer/ClientPortalScreen';
// #31 -- module Objections.
import ObjectionsScreen from '@/pages/dialer/ObjectionsScreen';
import BillingScreen from '@/pages/dialer/BillingScreen';
import SettingsScreen from '@/pages/dialer/SettingsScreen';
import CallHistoryScreen from '@/pages/dialer/CallHistoryScreen';
import DebugErrorBoundary from '@/lib/DebugErrorBoundary';
import ThemeShowcase from '@/pages/ThemeShowcase';
import Developers from '@/pages/Developers';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { platformRedirectRoutes } from '@/routes/platformRedirects';

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : <></>;

// ADR-031 — members view team management at /team (DialerLayout, below).
// DUP-3 (W6): /team also serves the super admin, in the back-office shell
// (TeamShell), reached from an organisation row that makes it active.
/** Client portal pages (viewer space, F4). W5 (DUP-4): one portal, the
 * former Client* pages redirect to its tabs. */
const PORTAL_PAGES = new Set(['ClientDashboard']);
// DUP-3 (W6): TeamManagement stays listed only so the generic loop below never mounts it bare;
// it is no longer a back-office page (/TeamManagement redirects to /team, TeamShell).
// AdminClientDetail, AdminCalendly and TwilioDiagnostic are retired (W6): platformRedirectRoutes() sends them on.
const ADMIN_PAGES = ['AdminNotifications', 'AdminAmdEngine', 'AdminCallingProvider', 'TwilioConfig', 'TeamManagement'];

// The admin space admits tenant_admin, but these pages call APIs that only a
// super admin may use (requireSuperAdmin on admin/organisations, admin/audit-logs,
// admin/diagnostics/twilio, twilio/admin/*): a tenant_admin got an empty table or
// "Erreur de chargement" (reconcile finding F3, tour 2026-09-27). RequireRole lets
// a super admin through unconditionally, so an empty role list = super admin only.
// QW-h9 (W6): AdminNotifications broadcasts across organisations, a super admin API.
const SUPER_ADMIN_ONLY_PAGES = new Set(['AdminOrganisations', 'AdminAuditLogs', 'TwilioConfig', 'AdminAmdEngine', 'AdminCallingProvider', 'AdminNotifications']);
const superAdminOnly = (element) => <RequireRoleV2 roles={[]}>{element}</RequireRoleV2>;

const LayoutWrapper = ({ children, currentPageName }) => Layout ?
  <Layout currentPageName={currentPageName}>{children}</Layout>
  : <>{children}</>;

const AuthenticatedApp = () => {
  const { t } = useTranslation(['common']);
  const { isLoadingAuth, isLoadingPublicSettings, authError, isAuthenticated, navigateToLogin } = useAuth();

  // Show loading spinner while checking app public settings or auth
  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={
        <LayoutWrapper currentPageName={mainPageKey}>
          <MainPage />
        </LayoutWrapper>
      } />

      {/* Design-system showcase (no layout wrapper): dev or super admin only (B3-theme). */}
      <Route path="/theme" element={<DevOrSuperAdmin><ThemeShowcase /></DevOrSuperAdmin>} />

      {/* Pages légales publiques — accessibles sans auth (requis par la
          vérification Google OAuth + standard SaaS). Wrapped in LayoutWrapper
          pour bénéficier du footer global avec liens croisés. */}
      <Route path="/confidential" element={
        <LayoutWrapper currentPageName="confidential">
          <PrivacyPolicy />
        </LayoutWrapper>
      } />
      <Route path="/cgu" element={
        <LayoutWrapper currentPageName="cgu">
          <TermsOfService />
        </LayoutWrapper>
      } />

      {/* DX-1 (W4): the public API guide, linked from Paramètres > Clés d'API. */}
      <Route path="/developers" element={
        <LayoutWrapper currentPageName="developers">
          <Developers />
        </LayoutWrapper>
      } />

      {/* DUP-7: old sign-in addresses, redirected by the router (?next kept). */}
      <Route path="/CallerLogin" element={<LegacyLoginRedirect />} />
      <Route path="/ClientLogin" element={<LegacyLoginRedirect />} />

      {/* Password reset: public, outside any layout, like /Login. */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Super-admin / tenant-admin space — wrapped in SuperAdminLayout so
          every admin page has nav + logout + breadcrumb */}
      <Route
        element={
          <RequireRoleV2 roles={['tenant_admin']}>
            <SuperAdminLayout />
          </RequireRoleV2>
        }
      >
        <Route path="/AdminOrganisations" element={superAdminOnly(<AdminOrganisations />)} />
        <Route path="/AdminAuditLogs" element={superAdminOnly(<AdminAuditLogs />)} />
        {/* W7 (SU-6, SU-7): tenant admin (own organisation) and super admin. */}
        <Route path="/AdminPermissions" element={<AdminPermissions />} />
        <Route path="/AdminSupervision" element={<AdminSupervision />} />
        <Route path="/AdminClientErrors" element={superAdminOnly(<AdminClientErrors />)} />
        <Route path="/AdminPlatformHealth" element={superAdminOnly(<AdminPlatformHealth />)} />
        {/* DUP-3 (W6): OrganisationMembers deleted; its links open the organisation's team. */}
        <Route path="/organisations/:organisationId/members" element={<OrganisationMembersRedirect />} />
        {ADMIN_PAGES.map((path) => {
          const Page = Pages[path];
          // i18n-exempt: a route name, not text
          if (!Page || path === 'TeamManagement') return null;
          return (
            <Route
              key={path}
              path={`/${path}`}
              element={SUPER_ADMIN_ONLY_PAGES.has(path) ? superAdminOnly(<Page />) : <Page />}
            />
          );
        })}
      </Route>

      {/* VF: Dialer multi-screen routing */}
      <Route path="/ParallelDialer" element={<RequireSpace space="dialer"><DebugErrorBoundary><DialerProvider><DialerLayout /></DialerProvider></DebugErrorBoundary></RequireSpace>}>
        <Route index element={<ContactsScreen />} />
        <Route path="home" element={<HomeScreen />} />
        <Route path="contacts" element={<ContactsScreen />} />
        <Route path="followups" element={<FollowupsScreen />} />
        <Route path="sms" element={<SmsScreen />} />
        <Route path="analytics" element={<AnalyticsScreen />} />
        <Route path="ai-chat" element={<ToolsScreen />} />
        <Route path="tools" element={<ToolsScreen />} />
        <Route path="crm" element={<CRMScreen />} />
        <Route path="client-portal" element={<RequireRoleV2 roles={['tenant_admin']}><ClientPortalScreen /></RequireRoleV2>} />
        <Route path="settings" element={<SettingsScreen />} />
        <Route path="billing" element={<BillingScreen />} />
        <Route path="call-history" element={<CallHistoryScreen />} />
        {/* #31 — module Objections. */}
        <Route path="objections" element={<ObjectionsScreen />} />
      </Route>

      {/* /team — page partagée (membres + clients + assignations) qui réutilise
          le DialerLayout (sidebar / customer switcher / avatar). URL courte
          parce que cette page n'est pas spécifique au dialer. */}
      <Route path="/team" element={<TeamShell />}>
        <Route index element={<TeamManagement />} />
      </Route>
      <Route path="/TeamManagement" element={<TeamManagementRedirect />} />

      {Object.entries(Pages).map(([path, Page]) => {
        // Admin pages are mounted above inside <SuperAdminLayout> — skip here.
        if (ADMIN_PAGES.includes(path)) return null;
        const page = (
          <LayoutWrapper currentPageName={path}>
            <Page />
          </LayoutWrapper>
        );
        // F4: the client portal has its guard. A non-viewer used to get a
        // blank page (return null); it now lands on its own space.
        // B3-SocialMedia: the social kit is an internal tool, super admin only.
        const pageElement = PORTAL_PAGES.has(path) ? <RequireSpace space="portal">{page}</RequireSpace>
          // i18n-exempt: a route name, not text
          : path === 'SocialMedia' ? superAdminOnly(page) : page;
        return <Route key={path} path={`/${path}`} element={pageElement} />;
      })}
      {/* W5 (DUP-4, DUP-7): the five stand-alone portal pages became tabs. */}
      {PORTAL_REDIRECTS.map(([page, tab]) => (
        <Route key={page} path={`/${page}`} element={<Navigate to={portalTabUrl(tab)} replace />} />
      ))}
      {/* W6: retired back-office pages land on their replacement (DUP-7). */}
      {platformRedirectRoutes()}
      <Route path="/unauthorized" element={<ErrorPage kind="unauthorized" />} />
      <Route path="*" element={<ErrorPage kind="notFound" />} />
    </Routes>
  );
};


function App() {

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <NavigationTracker />
          <AuthBootstrap />
          <AuthenticatedApp />
        </Router>
        <Toaster />
        <SonnerToaster
          position="top-right"
          richColors
          closeButton
          toastOptions={{
            classNames: {
              toast: 'border border-slate-200 shadow-lg',
            },
          }}
        />
        <VisualEditAgent />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App
